import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzBtwDataService,
  BtwOverzicht,
  BtwOverzichtFrequentie,
  ValueAddedTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {right} from '@popperjs/core';
import {debounceTime, filter, merge, Observable, shareReplay, take} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-overzicht',
  templateUrl: './overzicht.component.html',
  styleUrls: ['./overzicht.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    AsyncPipe
  ]
})
export class OverzichtComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  protected readonly right = right;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzBtwDataService);

  public btwOverzichtFg: FormGroup = new FormGroup({
    jaar: new FormControl<number | null>(null),
    initialenBelastingplichtige: new FormControl<string | null>(null),
    tussenvoegselBelastingplichtige: new FormControl<string | null>(null),
    achternaamBelastingplichtige: new FormControl<string | null>(null),
    omzetbelastingNummer: new FormControl<string | null>(null),
    naamContactPersoon: new FormControl<string | null>(null),
    telefoonnummer: new FormControl<string | null>(null),
    functie: new FormControl<string | null>(null),
    frequentie: new FormControl<BtwOverzichtFrequentie | null>(null),
    omzetbelastingNummerFE: new FormControl<string | null>(null),
    isFiscaleEenheid: new FormControl<boolean | null>(false),
    btwAangiftes: new FormControl<unknown[]>([]),
    cumulatieveAfdrachten: new FormControl<number | null>(0),
    berekening: new FormGroup({
      verschuldigdeBtwMaand1: new FormControl<number | null>(null),
      verschuldigdeBtwMaand2: new FormControl<number | null>(null),
      verschuldigdeBtwMaand3: new FormControl<number | null>(null),
      verschuldigdeBtwMaand4: new FormControl<number | null>(null),
      verschuldigdeBtwMaand5: new FormControl<number | null>(null),
      verschuldigdeBtwMaand6: new FormControl<number | null>(null),
      verschuldigdeBtwMaand7: new FormControl<number | null>(null),
      verschuldigdeBtwMaand8: new FormControl<number | null>(null),
      verschuldigdeBtwMaand9: new FormControl<number | null>(null),
      verschuldigdeBtwMaand10: new FormControl<number | null>(null),
      verschuldigdeBtwMaand11: new FormControl<number | null>(null),
      verschuldigdeBtwMaand12: new FormControl<number | null>(null),
      voorbelastingBtwMaand1: new FormControl<number | null>(null),
      voorbelastingBtwMaand2: new FormControl<number | null>(null),
      voorbelastingBtwMaand3: new FormControl<number | null>(null),
      voorbelastingBtwMaand4: new FormControl<number | null>(null),
      voorbelastingBtwMaand5: new FormControl<number | null>(null),
      voorbelastingBtwMaand6: new FormControl<number | null>(null),
      voorbelastingBtwMaand7: new FormControl<number | null>(null),
      voorbelastingBtwMaand8: new FormControl<number | null>(null),
      voorbelastingBtwMaand9: new FormControl<number | null>(null),
      voorbelastingBtwMaand10: new FormControl<number | null>(null),
      voorbelastingBtwMaand11: new FormControl<number | null>(null),
      voorbelastingBtwMaand12: new FormControl<number | null>(null),
      afdrachtMaand1: new FormControl<number | null>(null),
      afdrachtMaand2: new FormControl<number | null>(null),
      afdrachtMaand3: new FormControl<number | null>(null),
      afdrachtMaand4: new FormControl<number | null>(null),
      afdrachtMaand5: new FormControl<number | null>(null),
      afdrachtMaand6: new FormControl<number | null>(null),
      afdrachtMaand7: new FormControl<number | null>(null),
      afdrachtMaand8: new FormControl<number | null>(null),
      afdrachtMaand9: new FormControl<number | null>(null),
      afdrachtMaand10: new FormControl<number | null>(null),
      afdrachtMaand11: new FormControl<number | null>(null),
      afdrachtMaand12: new FormControl<number | null>(null),
      verschuldigdeBtwKwartaal1: new FormControl<number | null>(null),
      verschuldigdeBtwKwartaal2: new FormControl<number | null>(null),
      verschuldigdeBtwKwartaal3: new FormControl<number | null>(null),
      verschuldigdeBtwKwartaal4: new FormControl<number | null>(null),
      voorbelastingBtwKwartaal1: new FormControl<number | null>(null),
      voorbelastingBtwKwartaal2: new FormControl<number | null>(null),
      voorbelastingBtwKwartaal3: new FormControl<number | null>(null),
      voorbelastingBtwKwartaal4: new FormControl<number | null>(null),
      afdrachtKwartaal1: new FormControl<number | null>(null),
      afdrachtKwartaal2: new FormControl<number | null>(null),
      afdrachtKwartaal3: new FormControl<number | null>(null),
      afdrachtKwartaal4: new FormControl<number | null>(null),
      verschuldigdeBtwJaar: new FormControl<number | null>(null),
      voorbelastingBtwJaar: new FormControl<number | null>(null),
      afdrachtJaar: new FormControl<number | null>(null),
      totaalVerschuldigd: new FormControl<number | null>(null),
      totaalVoorbelasting: new FormControl<number | null>(null),
      totaalAangiften: new FormControl<number | null>(null),
      totaalSuppleties: new FormControl<number | null>(null),
      verschuldigdeOmzetbelasting: new FormControl<number | null>(null),
    })
  });

  public isMaandAangifte = false;
  public isKwartaalAangifte = true;
  public isJaarAangifte = false;

  readonly valueAddedTaxEntity$: Observable<ValueAddedTaxEntity> =
    this.dataService.valueAddedTaxEntity.pipe(
      filter((data): data is ValueAddedTaxEntity => !!data?.overzicht),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.disableReadonlyControls();

    this.valueAddedTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.updateForm(data);
    });

    merge(this.btwOverzichtFg.valueChanges).pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
      this.dataService.calculateLatestValueAddedTaxEntity().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }

  private disableReadonlyControls(): void {
    this.btwOverzichtFg.get('berekening')?.disable({emitEvent: false});
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.btwOverzichtFg.updateValueAndValidity({emitEvent: false});
    const raw = this.btwOverzichtFg.getRawValue();

    this.dataService.patchValueAddedTaxEntity(current => {
      if (!current.overzicht) {
        return current;
      }

      return {
        ...current,
        overzicht: {
          ...current.overzicht,
          cumulatieveAfdrachten: raw.cumulatieveAfdrachten ?? undefined,
        } as BtwOverzicht
      } as ValueAddedTaxEntity;
    });
  }

  private updateForm(value: ValueAddedTaxEntity): void {
    const overzicht = value.overzicht;

    if (!overzicht) {
      return;
    }

    this.btwOverzichtFg.patchValue(overzicht, {emitEvent: false});
    this.isMaandAangifte = overzicht.frequentie === BtwOverzichtFrequentie.MAAND;
    this.isKwartaalAangifte = overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL;
    this.isJaarAangifte = overzicht.frequentie === BtwOverzichtFrequentie.JAAR;
  }
}
